// src/utils/tableSearch.ts

export type SearchKey = string | string[];

type ColumnLike<T> = {
    key: keyof T | string[];
};

function normalizeText(value: unknown): string {
    if (value === null || value === undefined) return "";

    if (typeof value === "string") {
        return value.trim().toLowerCase();
    }

    if (typeof value === "number" || typeof value === "boolean") {
        return String(value).toLowerCase();
    }

    if (value instanceof Date) {
        return value.toISOString().toLowerCase();
    }

    return "";
}

/**
 * Soporta:
 * - "nombre"
 * - "cliente/nombre"
 * - ["nombre", "apellidos", "email"]  -> columna compuesta
 */
export function getValueByKeyPath(obj: any, key: SearchKey): unknown {
    if (!obj) return undefined;

    // Columna compuesta -> devolvemos array de valores
    if (Array.isArray(key)) {
        return key.map((k) => getValueByKeyPath(obj, k));
    }

    // Path anidado: "cliente/nombre"
    if (typeof key === "string" && key.includes("/")) {
        return key.split("/").reduce((acc: any, part) => acc?.[part], obj);
    }

    return obj?.[key];
}

/**
 * Convierte un valor cualquiera en una lista de textos buscables.
 * Recorre arrays y objetos para evitar perder coincidencias.
 */
export function flattenToSearchStrings(
    value: unknown,
    visited = new WeakSet<object>()
): string[] {
    if (value === null || value === undefined) return [];

    if (
        typeof value === "string" ||
        typeof value === "number" ||
        typeof value === "boolean" ||
        value instanceof Date
    ) {
        const normalized = normalizeText(value);
        return normalized ? [normalized] : [];
    }

    if (Array.isArray(value)) {
        return value.flatMap((item) => flattenToSearchStrings(item, visited));
    }

    if (typeof value === "object") {
        if (visited.has(value as object)) return [];
        visited.add(value as object);

        return Object.values(value as Record<string, unknown>).flatMap((item) =>
            flattenToSearchStrings(item, visited)
        );
    }

    return [];
}

/**
 * Devuelve las keys a buscar:
 * 1) searchTerms si existe
 * 2) columns.map(c => c.key) si no existe
 */
export function resolveSearchKeys<T>(
    searchTerms: string[] | undefined,
    columns: ColumnLike<T>[]
): SearchKey[] {
    if (searchTerms && searchTerms.length > 0) {
        return searchTerms;
    }

    return columns.map((c) => c.key as SearchKey);
}

/**
 * Aplica búsqueda sobre un dataset.
 */
export function applyTableSearch<T>(
    data: T[],
    searchTerm: string,
    options: {
        searchTerms?: string[];
        columns: ColumnLike<T>[];
    }
): T[] {
    const normalizedTerm = normalizeText(searchTerm);

    if (!normalizedTerm) return data;

    const keysToSearch = resolveSearchKeys(
        options.searchTerms,
        options.columns
    );

    return data.filter((row) => {
        const values = keysToSearch.flatMap((key) =>
            flattenToSearchStrings(getValueByKeyPath(row, key))
        );

        return values.some((text) => text.includes(normalizedTerm));
    });
}
